// src/pages/GeneralLedgerPage.tsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import 'react-datepicker/dist/react-datepicker.css';
import { useCompany } from '../components/CompanySelector';
import {
  GeneralLedgerLine,
  ReportResult,
  JournalEntryDetail,
} from '../dto/reports.dto'; // Import DTO types
import {
  formatCurrency,
  formatDate,
  // sumGeneralLedgerTotals,
} from '../utils/helpers';
import ReportTable from '../components/ReportTable';
import LoadingSpinner from '../components/LoadingSpinner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

const GeneralLedgerPage: React.FC = () => {
  const { selectedCompany } = useCompany();
  const [startDate, setStartDate] = useState<Date | null>(new Date(new Date().getFullYear(), new Date().getMonth(), 1)); // 1st of current month
  const [endDate, setEndDate] = useState<Date | null>(new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate())); // Today
  const [includeInitialBalance, setIncludeInitialBalance] = useState<boolean>(false);
  const [reportData, setReportData] = useState<GeneralLedgerLine[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [showJournalDetailsModal, setShowJournalDetailsModal] = useState<boolean>(false);
  const [journalDetails, setJournalDetails] = useState<JournalEntryDetail[] | null>(null);
  const [journalDetailsLoading, setJournalDetailsLoading] = useState<boolean>(false);
  const [selectedAccountForDetails, setSelectedAccountForDetails] = useState<any>(null); // Store account info for modal

  // State for filter options
  const [accounts, setAccounts] = useState<{ id: number; code: string; name: string }[]>([]);
  const [selectedAccounts, setSelectedAccounts] = useState<{ code: string; name: string }[]>([]);
  const [branches, setBranches] = useState<{ id: number; name: string }[]>([]);
  const [selectedBranches, setSelectedBranches] = useState<{ id: number; name: string }[]>([]);

  useEffect(() => {
    if (selectedCompany) {
      axios
          .get(`${BACKEND_URL}/accounts?companyId=${selectedCompany.id}`)
          .then((res) => setAccounts(res.data))
          .catch((err) => console.error('Failed to fetch accounts', err));
    }
  }, [selectedCompany]);

  const accountOptions = accounts.map(account => ({
    value: account.code,
    label: `${account.code} - ${account.name}`,
  }));

  useEffect(() => {
    axios
        .get(`${BACKEND_URL}/branches`)
        .then((res) => setBranches(res.data))
        .catch((err) => console.error('Failed to fetch branches', err));
  }, []);

  const branchOptions = branches.map(branch => ({
    value: branch.id,
    label: branch.name,
  }));

  const fetchGeneralLedger = async () => {
    if (!selectedCompany || !startDate || !endDate) {
      setError('Please select a company and valid date range.');
      setReportData(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Build params with URLSearchParams for proper handling of array parameters
      const params = new URLSearchParams();
      params.append('companyId', selectedCompany.id.toString());
      params.append('startDate', formatDate(startDate));
      params.append('endDate', formatDate(endDate));
      params.append('includeInitialBalance', includeInitialBalance.toString());

      // Add account code, branch IDs, to params if selected
      selectedAccounts.forEach(account => {
        params.append('accountCodes', account.code);
      });
      selectedBranches.forEach(branch => {
        params.append('branchIds', branch.id.toString());
      });

      const response = await axios.get<ReportResult<GeneralLedgerLine[]>>(
          `${BACKEND_URL}/reports/general-ledger?${params.toString()}`
      );
      setReportData(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch General Ledger.');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // if (selectedCompany && startDate && endDate) {
    //   fetchGeneralLedger();
    // }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany, startDate, endDate, includeInitialBalance]); // Re-fetch when these change

  const handleExport = async () => {
    if (!reportData) {
      alert('No data to export!');
      return;
    }

    try {
      const response = await axios.post(
        `${BACKEND_URL}/reports/export-excel`,
        {
          reportType: 'general-ledger',
          companyId: selectedCompany?.id,
          startDate: startDate ? formatDate(startDate) : undefined,
          endDate: endDate ? formatDate(endDate) : undefined,
          includeInitialBalance: includeInitialBalance,
          reportName: 'General Ledger',
          // Optionally send the data directly if it's already fetched and processed client-side
          // data: reportData, // For GL, better to let backend re-fetch to ensure consistency and full details
          branchIds: selectedBranches.length > 0 ? selectedBranches.map(branch => branch.id) : [],
          accountCodes: selectedAccounts.length > 0 ? selectedAccounts.map(account => account.code) : [],
        },
        {
          responseType: 'blob', // Important for downloading files
        }
      );

      // Create a blob from the response data and create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `General_Ledger_${selectedCompany?.name}_${formatDate(startDate!)}-${formatDate(endDate!)}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export report.');
    }
  };

  const handleExportJournalDetails = async () => {
    if (!selectedCompany || !startDate || !endDate || !selectedAccountForDetails) {
      alert('Company, dates, and account must be selected to export details.');
      return;
    }

    const accountId = Number.parseInt(selectedAccountForDetails.accountCode, 10);
    if (Number.isNaN(accountId)) {
      alert('Account ID is invalid for export.');
      return;
    }

    try {
      const response = await axios.post(
        `${BACKEND_URL}/reports/export-excel`,
        {
          reportType: 'journal-details',
          reportName: `Journal Details - ${selectedAccountForDetails.accountCode} ${selectedAccountForDetails.accountName}`,
          companyId: selectedCompany.id,
          accountId,
          startDate: formatDate(startDate),
          endDate: formatDate(endDate),
          includeInitialBalance: includeInitialBalance.toString(),
          branchIds: selectedBranches.length > 0 ? selectedBranches.map((b) => b.id) : [],
        },
        {
          responseType: 'blob',
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `Journal_Details_${selectedCompany?.name}_${selectedAccountForDetails.accountCode}_${formatDate(startDate)}-${formatDate(endDate)}.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export journal details failed:', err);
      alert('Failed to export journal details.');
    }
  };

  const handleViewJournalDetails = async (account: GeneralLedgerLine) => {
    if (!selectedCompany || !startDate || !endDate) {
      alert('Company and dates must be selected to view details.');
      return;
    }
    setSelectedAccountForDetails(account);
    setJournalDetailsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append('accountId', account.accountCode || '');
      params.append('companyId', selectedCompany.id.toString());
      params.append('startDate', formatDate(startDate));
      params.append('endDate', formatDate(endDate));
      params.append('includeInitialBalance', includeInitialBalance.toString());

      selectedBranches.forEach(branch => {
        params.append('branchIds', branch.id.toString());
      });

      const response = await axios.get<JournalEntryDetail[]>(
          `${BACKEND_URL}/reports/journal-details?${params.toString()}`
      );
      setJournalDetails(response.data);
      setShowJournalDetailsModal(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch journal details.');
      setJournalDetails(null);
    } finally {
      setJournalDetailsLoading(false);
    }
  };

  const glColumns = [
    {
      header: 'Account Code',
      accessor: (row: GeneralLedgerLine) => row.type === 'account_header' ? row.accountCode : '',
      className: 'font-semibold'
    },
    {
      header: 'Account Name',
      accessor: (row: GeneralLedgerLine) => row.type === 'account_header' ? row.accountName : '',
      className: 'font-semibold'
    },
    // {
    //   header: 'Date',
    //   accessor: (row: GeneralLedgerLine) => row.type === 'journal_item' ? formatDate(row.date) : '',
    // },
    // {
    //   header: 'Reference',
    //   accessor: (row: GeneralLedgerLine) => row.type === 'journal_item' ? row.ref : '',
    // },
    // {
    //   header: 'Description',
    //   accessor: (row: GeneralLedgerLine) => row.type === 'journal_item' ? row.description : (row.type === 'account_header' ? `Initial Balance: ${formatCurrency(row.initialBalance)}` : ''),
    // },
    {
      header: 'Debit',
      accessor: (row: GeneralLedgerLine) => row.type === 'journal_item' ? formatCurrency(row.debit) : (row.type === 'account_header' ? formatCurrency(row.debitPeriod) : ''),
      className: 'text-right',
    },
    {
      header: 'Credit',
      accessor: (row: GeneralLedgerLine) => row.type === 'journal_item' ? formatCurrency(row.credit) : (row.type === 'account_header' ? formatCurrency(row.creditPeriod) : ''),
      className: 'text-right',
    },
    {
      header: 'Balance',
      accessor: (row: GeneralLedgerLine) => formatCurrency(row.type === 'journal_item' ? row.balance : row.finalBalance),
      className: 'text-right',
    },
    {
      header: 'Actions',
      accessor: (row: GeneralLedgerLine) => row.type === 'account_header' ? (
        <button
          onClick={() => handleViewJournalDetails(row)}
          className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 text-sm"
        >
          View Details
        </button>
      ) : null,
      className: 'text-center',
    }
  ];

  // const journalDetailsColumns = [
  //   { header: 'Date', accessor: (row: JournalEntryDetail) => formatDate(row.date) },
  //   { header: 'Reference', accessor: 'ref' },
  //   { header: 'Account Code', accessor: 'accountCode' },
  //   { header: 'Account Name', accessor: 'accountName' },
  //   { header: 'Debit', accessor: (row: JournalEntryDetail) => formatCurrency(row.debit), className: 'text-right' },
  //   { header: 'Credit', accessor: (row: JournalEntryDetail) => formatCurrency(row.credit), className: 'text-right' },
  // ];
  
  const journalDetailsColumns = [
  { header: 'Date', accessor: (row: JournalEntryDetail) => formatDate(row.date) },
  { header: 'Journal', accessor: 'ref' }, // Assuming 'ref' is journal reference
  { header: 'Description', accessor: 'label' }, // Assuming 'description' field
  { header: 'Branch', accessor: (row: JournalEntryDetail) => row.branch ?? '' },
  { header: 'Partner', accessor: (row: JournalEntryDetail) => row.partner ?? '' },
  { header: 'Debit', accessor: (row: JournalEntryDetail) => formatCurrency(row.debit), className: 'text-right' },
  { header: 'Credit', accessor: (row: JournalEntryDetail) => formatCurrency(row.credit), className: 'text-right' },
  { header: 'Balance', accessor: (row: JournalEntryDetail) => formatCurrency(row.balance), className: 'text-right' },
];

  // const totals = reportData ? sumGeneralLedgerTotals(reportData) : { totalDebit: 0, totalCredit: 0 };

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-3xl font-bold mb-6 text-gray-800">General Ledger Report</h2>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-6 items-start">
        <div className="col-span-12 md:col-span-2">
          <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">Start Date:</label>
          <DatePicker
            selected={startDate}
            onChange={(date: Date | null) => setStartDate(date)}
            dateFormat="yyyy-MM-dd"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2"
          />
        </div>
        <div className="col-span-12 md:col-span-2">
          <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">End Date:</label>
          <DatePicker
            selected={endDate}
            onChange={(date: Date | null) => setEndDate(date)}
            dateFormat="yyyy-MM-dd"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2"
          />
        </div>
        <div className="col-span-12 md:col-span-2">
          <label htmlFor="accountSelector" className="block text-sm font-medium text-gray-700">Accounts:</label>
          <Select
              id="accountSelector"
              options={accountOptions}
              isMulti
              placeholder="All Accounts"
              value={accountOptions.filter(option => selectedAccounts.some(a => a.code === option.value))}
              onChange={(selectedOptions) => {
                setSelectedAccounts(selectedOptions.map((option) => ({
                  code: option.value,
                  name: option.label.split(' - ')[1]
                })));
              }}
              className="mt-1"
          />
        </div>
        <div className="col-span-12 md:col-span-2">
          <label htmlFor="branchSelector" className="block text-sm font-medium text-gray-700">Branch:</label>
          <Select
            id="branchSelector"
            options={branchOptions}
            isMulti
            placeholder="All Branches"
            value={branchOptions.filter(option => selectedBranches.some(b => b.id === option.value))}
            onChange={(selectedOptions) => {
              setSelectedBranches(selectedOptions.map((option) => ({ id: option.value, name: option.label })));
            }}
            className="mt-1"
          />
        </div>
        <div className="col-span-12 md:col-span-2 md:mt-5 flex">
          <div className="flex items-center md:ml-2 md:mr-1 h-10">
            <input
              id="includeInitialBalance"
              type="checkbox"
              checked={includeInitialBalance}
              onChange={(e) => setIncludeInitialBalance(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="includeInitialBalance" className="ml-2 block text-sm text-gray-900">
              Include Initial Balance
            </label>
          </div>
        </div>
        <div className="col-span-12 md:col-span-2 md:mt-5">
          <button
            onClick={fetchGeneralLedger}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            disabled={loading || !selectedCompany}
          >
            {loading ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">{error}</div>}

      {loading ? (
        <LoadingSpinner />
      ) : reportData && reportData.length > 0 ? (
        <>
          <div className="mb-4">
            <button
              onClick={handleExport}
              className="bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            >
              Export to XLSX
            </button>
          </div>
          <ReportTable columns={glColumns} data={reportData} />
          {/* <div className="mt-4 p-4 bg-gray-50 rounded-md shadow-inner text-right font-bold text-lg">
            <p>Total Debit for Period: {formatCurrency(totals.totalDebit)}</p>
            <p>Total Credit for Period: {formatCurrency(totals.totalCredit)}</p>
          </div> */}
        </>
      ) : (
        <p className="text-gray-500 text-center">No data available for the selected criteria. Please generate the report.</p>
      )}

      {/* Journal Details Modal */}
      {showJournalDetailsModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-8xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Journal Details for {selectedAccountForDetails?.accountName} ({selectedAccountForDetails?.accountCode})</h3>
              <p className="text-gray-600">Period: {formatDate(startDate!)} - {formatDate(endDate!)}</p>
              {selectedBranches.length > 0 && (
                  <p className="text-gray-600">Branches: {selectedBranches.map(b => b.name).join(', ')}</p>
              )}
              {selectedAccounts.length > 0 && (
                  <p className="text-gray-600">Account Filter Applied</p>
              )}
              <div className="mt-4">
                <button
                  onClick={handleExportJournalDetails}
                  className="bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                  disabled={journalDetailsLoading || !journalDetails || journalDetails.length === 0}
                >
                  Export to XLSX
                </button>
              </div>
            </div>
            <div className="p-6 flex-1 overflow-auto">
              {journalDetailsLoading ? (
                <LoadingSpinner />
              ) : journalDetails && journalDetails.length > 0 ? (
                <ReportTable columns={journalDetailsColumns} data={journalDetails} />
              ) : (
                <p className="text-gray-500 text-center">No journal details found for this period.</p>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowJournalDetailsModal(false)}
                className="bg-gray-300 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneralLedgerPage;